// src/components/CollectionActionsSidebar.jsx
// Sidebar d'actions de la fiche collection, regroupées en 4 familles
// (Œuvres / Partage & présentation / Exports & dossiers / Gestion).
// Le partage est consolidé en UN bouton « Partager ▾ » → Accès · Présentation · Dossier.
import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import ExportButton from "./shared/ExportButton";
import ExportButtonComplete from "./shared/ExportButtonComplete";
import SuccessionDossierButton from "./SuccessionDossierButton";
import ContinuityReportButton from "./ContinuityReportButton";
import InsuranceDossierButton from "./InsuranceDossierButton";
import ShareDossierButton from "./ShareDossierButton";
import PilotConsentCard from "./PilotConsentCard";
import { useCabinetText } from "../lib/cabinetI18n";

const BORDER = "#e5e3df";
const GOLD = "#a9843f";

const sectionTitle = {
  fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase",
  color: "#8a8378", fontFamily: "system-ui, sans-serif", fontWeight: 600,
  margin: "0 0 8px",
};
const rowBtn = {
  width: "100%", textAlign: "left", padding: "9px 12px", background: "#fff",
  border: `1px solid ${BORDER}`, borderRadius: 4, fontSize: 13, cursor: "pointer",
  fontFamily: "system-ui, sans-serif", fontWeight: 500, color: "#1a1a1a",
  display: "flex", alignItems: "center", gap: 8, marginBottom: 6,
};

function Section({ title, children }) {
  const kids = React.Children.toArray(children).filter(Boolean);
  if (kids.length === 0) return null;
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={sectionTitle}>{title}</div>
      {children}
    </div>
  );
}

export default function CollectionActionsSidebar({
  t,
  id, collectionName, ownerName,
  canEdit, isExpert, isShared,
  editUrl, presentationUrl, cabinetUrl,
  onLot, onPhone, onShareAccess, onSharePresentation, onExpertise, onReport, onOcr, onDelete,
}) {
  const { t: ct } = useCabinetText();
  const [shareMenu, setShareMenu] = useState(false);
  const shareRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (shareRef.current && !shareRef.current.contains(e.target)) setShareMenu(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <aside
      style={{
        flex: "0 0 250px", maxWidth: "100%", alignSelf: "flex-start",
        background: "#faf9f7", border: `1px solid ${BORDER}`, borderRadius: 8,
        padding: 16, position: "sticky", top: 16,
      }}
    >
      {/* Œuvres */}
      <Section title={t("round36.collectionDetail.sidebar.artworks", { defaultValue: "Œuvres" })}>
        {(canEdit || isExpert) && (
          <button type="button" style={rowBtn} onClick={onLot}>
            {t("round36.collectionDetail.actions.lot", { defaultValue: "Créer un lot" })}
          </button>
        )}
        {canEdit && (
          <button type="button" style={rowBtn} onClick={onPhone}>
            {t("mobileCapture.newArtwork.heading", { defaultValue: "Photographier une œuvre" })}
          </button>
        )}
      </Section>

      {/* Partage & présentation */}
      {!isShared && (
        <Section title={t("round36.collectionDetail.sidebar.share", { defaultValue: "Partage & présentation" })}>
          <Link to={presentationUrl} style={{ ...rowBtn, textDecoration: "none" }}>
            {t("round36.collectionDetail.sidebar.viewPresentation", { defaultValue: "Voir la présentation" })}
          </Link>
          {cabinetUrl && (
            <Link to={cabinetUrl} style={{ ...rowBtn, textDecoration: "none" }}>
              {ct("sidebarLink")}
            </Link>
          )}

          <div ref={shareRef} style={{ position: "relative" }}>
            <button
              type="button"
              style={{ ...rowBtn, borderColor: GOLD, color: GOLD, fontWeight: 600 }}
              onClick={() => setShareMenu((v) => !v)}
            >
              {t("round36.collectionDetail.actions.share", { defaultValue: "Partager" })} ▾
            </button>
            {shareMenu && (
              <div
                style={{
                  position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20,
                  background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 4,
                  boxShadow: "0 6px 18px rgba(0,0,0,0.08)", padding: 6, marginTop: -2,
                }}
              >
                <button type="button" style={{ ...rowBtn, border: "none", marginBottom: 2 }}
                  onClick={() => { setShareMenu(false); onShareAccess?.(); }}>
                  {t("round36.collectionDetail.share.access", { defaultValue: "Accès (droits)" })}
                </button>
                <button type="button" style={{ ...rowBtn, border: "none", marginBottom: 2 }}
                  onClick={() => { setShareMenu(false); onSharePresentation?.(); }}>
                  {t("round36.collectionDetail.share.presentation", { defaultValue: "Présentation" })}
                </button>
                <div onClick={() => setShareMenu(false)}>
                  <ShareDossierButton scopeType="collection" scopeId={id} collectionName={collectionName} />
                </div>
              </div>
            )}
          </div>
        </Section>
      )}

      {/* Exports & dossiers */}
      <Section title={t("round36.collectionDetail.sidebar.exports", { defaultValue: "Exports & dossiers" })}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <ExportButton collectionId={id} collectionName={collectionName} />
          <ExportButtonComplete collectionId={id} label={t("round36.collectionDetail.actions.exportComplete", { defaultValue: "Export complet (ZIP + images)" })} variant="btn-secondary-artsy" />
          <SuccessionDossierButton collectionId={id} collectionName={collectionName} ownerName={ownerName} />
          <ContinuityReportButton collectionId={id} ownerName={ownerName} />
          <InsuranceDossierButton collectionId={id} collectionName={collectionName} ownerName={ownerName} />
        </div>
      </Section>

      {/* Consentement pilote : seulement si la collection contient des œuvres venant d'une plateforme */}
      {canEdit && !isShared && <PilotConsentCard collectionId={id} canManage />}

      {/* Gestion */}
      <Section title={t("round36.collectionDetail.sidebar.manage", { defaultValue: "Gestion" })}>
        {canEdit && !isShared && (
          <Link to={editUrl} style={{ ...rowBtn, textDecoration: "none" }}>
            {t("round36.collectionDetail.actions.edit", { defaultValue: "Modifier" })}
          </Link>
        )}
        {isExpert && (
          <button type="button" style={rowBtn} onClick={onExpertise}>
            {t("round36.collectionDetail.actions.expertise", { defaultValue: "Générer une expertise" })}
          </button>
        )}
        {isExpert && (
          <button type="button" style={rowBtn} onClick={onReport}>
            {t("round36.collectionDetail.actions.report", { defaultValue: "Rapport d'expertise" })}
          </button>
        )}
        {isExpert && (
          <button type="button" style={rowBtn} onClick={onOcr}>
            {t("round36.collectionDetail.actions.ocr", { defaultValue: "OCR par lot" })}
          </button>
        )}
        {!isShared && (
          <button
            type="button"
            style={{ ...rowBtn, borderColor: "#e3c4c4", color: "#991b1b" }}
            onClick={onDelete}
          >
            {t("round36.collectionDetail.actions.delete", { defaultValue: "Supprimer" })}
          </button>
        )}
      </Section>
    </aside>
  );
}
